import { useRef, useState } from "react";
import { flexRender } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";

import Text from "./Text";
import { PascalCase } from "../utils";

const ROW_HEIGHT = 38;

const ProcessTable = ({ table, theme }) => {

    const isDark = theme.White !== "#FFFFFF";
    const hoverColor = isDark ? theme.FOURFOUR : theme.Ey;
    const [hoveredRow, setHoveredRow] = useState(null);
    const getColor = (accessorKey, original) => {

        if (accessorKey === 'entrega') {
            return theme[original.entregaColor];
        }
        switch(accessorKey) {
            case 'numOrdem':
            case 'cliente':
                return theme.ONED
            case 'ingreso':
            case 'actualizado':
                return theme.SEVENB
            default:
                return theme.ONED
        }
    }

    const rightAlignedKeys = ['entrega']
    const getAlignment = (accessorKey) => {
        return rightAlignedKeys.includes(accessorKey) ? 'flex-end' : 'flex-start';
    }
   
    const getColWidth = (key) => {
        switch(key) {
            case 'numOrdem':
            case 'recurso':
                return {flex: '1 1 132px', minWidth: '100px'};
            case 'cliente':
            case 'descripcion':
                return {flex: '3 1 348px', minWidth: '200px'};
            case 'ingreso':
            case 'elemento':
                return {flex: '2 1 232px', minWidth: '150px'};
            case 'estado':
            case 'inicio':
                return {flex: '2 1 232px', minWidth: '150px', paddingLeft: key === 'inicio' ? '20px' : '0'};
            case 'actualizado':
                return {flex: '2 1 120px', minWidth: '100px'};
            case 'fin':
                return {flex: '2 1 100px', minWidth: '80px'};
            case 'cantPlanif':
            case 'cantProd':
                return {flex: '.5 1 110px', minWidth: '90px'};
            case 'entrega':
                return {flex: '1 1 200px', minWidth: '100px'};
            default:
                return {flex: '1 1 100px', minWidth: '100px'};
        }
    }

    const styles = {
        row: {
            display: 'flex', width: '100%', height: '38px', alignItems: 'center',
            boxSizing: 'border-box'
        },
        colWrapper: (key) =>  ({
            ...getColWidth(key), display: 'flex', alignItems: 'center', boxSizing: 'border-box',
            padding: '0 16px'
        }),
        truncate: {
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%'
        }
    }

    const breakdownKeys = ['recurso', 'descripcion', 'elemento', 'inicio', 'fin', 'cantPlanif', 'cantProd'];
    const requirePascal = ['recurso', 'descripcion', 'elemento']

    const formatStr = (value) => {
        const result = PascalCase(value);
        return Array.isArray(result) ? result.join(', ') : result;
    }

    const containerRef = useRef(null);
    const {rows} = table.getRowModel();
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => containerRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 10,
        measureElement: (el) => el.getBoundingClientRect().height
    })

    return (
        <div ref={containerRef} style={{width: '100%', minWidth: 0, height: '500px', overflowY: 'auto', overflowX: 'auto'}}>
            <div style={{minWidth: 'min-content', maxWidth: '100%', height: `${rowVirtualizer.getTotalSize() + ROW_HEIGHT}px`, position: 'relative'}}>
                <div style={{position: 'sticky', top: 0, zIndex: 1, backgroundColor: isDark ? theme.Ey : theme.Ed}}>
                    {table.getHeaderGroups().map(headerGroup => (
                        <div key={headerGroup.id} style={{...styles.row, height: '38px', border: `0.5px solid ${theme.Ey}`, borderRadius: '4px'}}> 
                            {headerGroup.headers.map((header, i) => {
                                const accessorKey = header.column.columnDef.accessorKey;

                                const isSorted = header.column.getIsSorted();
                                const sortIcon = isSorted === 'asc' ? ' ▲' : isSorted === 'desc' ? ' ▼' : '';
                                return (
                                    <div key = {header.id} onClick={header.column.getToggleSortingHandler()} style={{...styles.colWrapper(accessorKey, i), height: '36px', textAlign: getAlignment(accessorKey), display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none'}}> 
                                        {header.isPlaceHolder ? null : (
                                            <>
                                                <Text size={15} weigth={500} color={theme.ONED}>{flexRender(header.column.columnDef.header, header.getContext())}</Text>
                                                <Text size={12} style={{marginLeft: '4px'}}>{sortIcon}</Text>
                                            </>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    ))}
                </div>
                {rowVirtualizer.getVirtualItems().map(virtualRow => {
                    const row = rows[virtualRow.index];
                    return (
                        <div key={row.id} ref={rowVirtualizer.measureElement} data-index={virtualRow.index} style={{position: 'absolute', top: ROW_HEIGHT, left: 0, width: '100%', transform: `translateY(${virtualRow.start}px)`}}>
                            <div onClick={row.getToggleExpandedHandler()} onMouseEnter={() => setHoveredRow(row.id)} onMouseLeave={() => setHoveredRow(null)} style={{...styles.row, backgroundColor: hoveredRow === row.id ? hoverColor : isDark ? theme.Ey : theme.Ed, border: `0.5px solid ${theme.Ey}`, borderRadius: '4px', cursor: 'pointer'}}>
                                {row.getVisibleCells().map((cell, i) => {
                                    const accessorKey = cell.column.columnDef.accessorKey;
                                    const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                                    return (
                                        <div key={cell.id} style={{...styles.colWrapper(accessorKey, i), justifyContent: getAlignment(accessorKey)}} title={cell.getValue()}>
                                            <Text color={getColor(accessorKey, row.original)} size={15} style={styles.truncate}>
                                                {requirePascal.includes(accessorKey) && typeof content === 'string' ? formatStr(content) : content}
                                            </Text>
                                        </div>
                                    )
                                })}
                            </div>
                            {row.getIsExpanded() && (
                                <div style={{width: '100%'}}>
                                    <div style={{...styles.row, height: '28px', backgroundColor: isDark ? theme.Ey : theme.Ed, border: `0.5px solid ${theme.Ey}`, borderRadius: '4px'}}>
                                        {breakdownKeys.map((key, i) => (
                                            <div key={key} style={{...styles.colWrapper(key, i)}}>
                                                <Text color={theme.Blue} style={styles.truncate} size={15}>
                                                    {key === 'cantPlanif' ? 'C. Planif' : key === 'cantProd' ? 'C. Prod' : PascalCase(key)}
                                                </Text>
                                            </div>
                                        ))}
                                    </div>
                                    {(row.original.detalles || []).map((det, index) => (
                                        <div key={`${row.id}-${index}`} style={{...styles.row, backgroundColor: isDark ? theme.Ey : theme.Ed, opacity: 0.5, border: `0.5px solid ${theme.Ey}`, borderRadius: '4px'}}>
                                            {breakdownKeys.map((key, i) => (
                                                <div key={key} style={{...styles.colWrapper(key, i)}} title={det[key]}>
                                                    <Text color={theme.THREEB} style={styles.truncate} size={15}>
                                                        {requirePascal.includes(key) ? PascalCase(det[key]) : det[key]}
                                                    </Text>
                                                </div>

                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
};

export default ProcessTable;